import { Component } from '@angular/core';
import {
  GridDataResult,
  DataStateChangeEvent,
  FilterService,
} from '@progress/kendo-angular-grid';
import {
  CompositeFilterDescriptor,
  SortDescriptor,
} from '@progress/kendo-data-query';
import {
  ColumnOptions,
  GridState,
  MessageService,
  LoaderService,
} from '@shared';
import { ReportService } from '../../../services';
import { GridRowZeroPrice } from '../../../models';

@Component({
  selector: 'app-zero-price-report',
  templateUrl: './zero-price-report.component.html',
  styleUrls: ['./zero-price-report.component.scss'],
})
export class ZeroPriceReportComponent {
  zeroPriceExceptions: GridDataResult = { data: [], total: 0 };

  columns: ColumnOptions[] = [];

  state!: GridState;

  pageSize: number = 10;

  fontSize: number = 0.875;

  initialSort: SortDescriptor[] = [
    { field: 'tierName', dir: 'asc' },
    { field: 'restaurantNo', dir: 'asc' },
    { field: 'category', dir: 'asc' },
    { field: 'itemName', dir: 'asc' },
  ];

  // Report type the checkbox list of app-custom-filter reads its values from
  reportType: string = 'pricing/zero-price';

  // Values checked on each column filter, so reopening the menu shows them
  filterCache: Map<string, string[]> = new Map<string, string[]>();

  constructor(
    private reportService: ReportService,
    private messageService: MessageService,
    private loaderService: LoaderService,
  ) {
    this.columns = new GridRowZeroPrice().getColumns();
  }

  ngOnInit() {
    this.setDefaultState();
  }

  setDefaultState(): void {
    this.state = new GridState(this.pageSize, this.initialSort);
    this.fetchReportData();
  }

  fetchReportData() {
    this.loaderService.show();

    this.reportService.getReportData(this.reportType, this.state).subscribe({
      next: (data) => {
        this.zeroPriceExceptions = data;

        this.zeroPriceExceptions.data = this.zeroPriceExceptions.data.map(
          (item) =>
            new GridRowZeroPrice(
              item.tierName,
              item.restaurantNo,
              item.category,
              item.itemName,
              item.componentName,
              item.componentType,
              item.currentPrice,
              item.parItemId,
              item.plu,
            ),
        );
        this.loaderService.hide();
      },
      error: (err) => {
        this.messageService.error(err);
        this.loaderService.hide();
      },
    });
  }

  onDataStateChanged(event: DataStateChangeEvent) {
    this.state = event as GridState;
    this.fetchReportData();
  }

  // A checked value on a column filter is one more OR of that same field, so
  // checking Combo and Modifier brings the rows of both categories
  onCustomFilterChange(
    column: string,
    event: string[],
    filterService: FilterService,
  ) {
    this.filterCache.set(column, event);

    filterService.filter({
      filters: event.map((value) => ({
        field: column,
        operator: 'eq',
        value,
      })),
      logic: 'or',
    });
  }

  // Drops from the cache the fields the user cleared on the filter menu
  filterChanged(event: CompositeFilterDescriptor) {
    if (event.filters.length === 0) {
      this.filterCache.clear();
    }

    for (const field of this.filterCache.keys()) {
      if (!GridState.hasField(event, field)) {
        this.filterCache.delete(field);
      }
    }
  }

  exportExceptions() {
    this.loaderService.show();
    this.reportService.exportExceptions('pricing/zero-price').subscribe({
      next: (data: Blob) => {
        const blob = new Blob([data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Zero_Price_Report_${new Date().toDateString()}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.loaderService.hide();
      },
      error: (err: string) => {
        this.loaderService.hide();
        this.messageService.error(err);
      },
    });
  }
}
